import {
  Box,
  Button,
  Card as ChakraCard,
  Flex,
  Icon,
  Spinner,
  Tag,
  Text,
  useColorModeValue,
  type BoxProps,
  type ButtonProps,
  type TableCellProps,
} from '@chakra-ui/react';
import { FiArrowLeft, FiPlus } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import type { ReactNode } from 'react';
import Card from 'components/card/Card';

/** Same outer wrapper as Home, Team, Ecosystem, Settings. */
export function SnapshotPageShell({ children }: { children: ReactNode }) {
  return (
    <Box w="100%" minH="100vh" mt="100px" px={{ base: 2, md: 8 }} py={4}>
      {children}
    </Box>
  );
}

/** Matches `Card` usage across admin (30px radius, theme shadow). */
export function SnapshotCard({ children, ...rest }: BoxProps) {
  return (
    <Card w="100%" p={{ base: 5, md: 8 }} borderRadius="30px" boxShadow="md" {...rest}>
      {children}
    </Card>
  );
}

/** Same shell as Ecosystem → Current Partners (`Partners.tsx`). */
export function SnapshotSectionCard({ children, ...rest }: BoxProps) {
  return (
    <ChakraCard w="100%" p="20px" borderRadius="12px" {...rest}>
      {children}
    </ChakraCard>
  );
}

export function SnapshotBackLink({
  to = '/admin/snapshots',
  mb = 4,
}: {
  to?: string;
  mb?: number;
}) {
  const navigate = useNavigate();
  const textColor = useColorModeValue('navy.700', 'white');
  return (
    <Button
      variant="ghost"
      size="sm"
      leftIcon={<Icon as={FiArrowLeft} />}
      color={textColor}
      fontWeight="500"
      mb={mb}
      px={0}
      _hover={{ bg: 'transparent', opacity: 0.8 }}
      onClick={() => navigate(to)}
    >
      Back to snapshots
    </Button>
  );
}

type SnapshotPageHeaderProps = {
  title: string;
  description?: ReactNode;
  meta?: ReactNode;
  action?: ReactNode;
};

/** Page title block aligned with Home / Team. */
export function SnapshotPageHeader({
  title,
  description,
  meta,
  action,
}: SnapshotPageHeaderProps) {
  const textColor = useColorModeValue('navy.700', 'white');
  return (
    <Flex
      justify="space-between"
      align="flex-start"
      gap={4}
      mb={6}
      direction={{ base: 'column', md: 'row' }}
      flexWrap="wrap"
    >
      <Box flex={1} minW={0}>
        <Text fontSize="2xl" fontWeight="700" color={textColor} mb={1}>
          {title}
        </Text>
        {description && (
          <Text color="gray.500" fontSize="md" lineHeight="1.6" maxW="720px">
            {description}
          </Text>
        )}
        {meta && (
          <Text color="gray.500" fontSize="sm" mt={2} maxW="720px" lineHeight="1.5">
            {meta}
          </Text>
        )}
      </Box>
      {action}
    </Flex>
  );
}

export function SnapshotPrimaryButton(props: ButtonProps) {
  return <Button variant="brand" {...props} />;
}

export function NewSnapshotButton({ onClick }: { onClick: () => void }) {
  return (
    <SnapshotPrimaryButton
      leftIcon={<Icon as={FiPlus} />}
      onClick={onClick}
      flexShrink={0}
    >
      New snapshot
    </SnapshotPrimaryButton>
  );
}

export function SnapshotTableColumnHeader({ children }: { children: ReactNode }) {
  return (
    <Text fontSize={{ sm: '10px', lg: '12px' }} color="gray.400">
      {children}
    </Text>
  );
}

/** Status pill aligned with `ConnectedPartnerTable` `StatusTag`. */
export function SnapshotStatusTag({
  label,
  bg,
  color,
}: {
  label: string;
  bg: string;
  color: string;
}) {
  return (
    <Tag
      w="fit-content"
      maxW="100%"
      whiteSpace="nowrap"
      h="30px"
      px="14px"
      borderRadius="15px"
      bg={bg}
      display="flex"
      alignItems="center"
      borderWidth={0}
    >
      <Text color={color} fontSize="14px" fontWeight="700" lineHeight="1">
        {label}
      </Text>
    </Tag>
  );
}

export function useSnapshotTableStyles() {
  const borderColor = useColorModeValue('gray.200', 'whiteAlpha.100');
  const textColor = useColorModeValue('navy.700', 'white');
  const cellProps: TableCellProps = {
    borderColor,
    fontSize: { sm: '14px' },
    minW: { sm: '150px', md: '200px', lg: 'auto' },
  };
  const rowHoverBg = useColorModeValue('gray.50', 'whiteAlpha.50');
  return { borderColor, textColor, cellProps, rowHoverBg };
}

export function SnapshotPageSpinner() {
  return (
    <Flex justify="center" py={16}>
      <Spinner
        thickness="4px"
        speed="0.65s"
        emptyColor="gray.200"
        color="brand.400"
        size="lg"
      />
    </Flex>
  );
}
